var __jsx = React.createElement;
import React, { useState, useRef, useEffect } from "react";
import { cn } from "@lib/cn";
import CustomInput from "../custom-input";
import { IconButton } from "../buttons";
import Icons from "@modules/icons";
import { useMediaQuery } from "react-responsive";
import { BREAKPOINTS } from "@lib/constants";
import useCssModules from "@lib/hooks/use-css-modules";
const CustomAutocomplete = _ref => {
  let {
    identifier,
    label,
    onSelectOption,
    value,
    className = "",
    getOptions = () => Promise.resolve([]),
    inputLabelIcon,
    optionsIcon,
    hasErrors = false,
    errorMessage = "",
    placeholder,
    onClearValue,
    disable = false,
    customInputClass,
    dataTestId,
    onClick,
    disablePopper,
    disableLabelAnimation = false,
    disableXButton = false,
    noOptionsText = "No options",
    useBorderlessStyle = false,
    groupIcon,
    groupKey
  } = _ref;
  const {
    0: inputValue,
    1: setInputValue
  } = useState("");
  const {
    0: options,
    1: setOptions
  } = useState([]);
  const {
    0: isOpen,
    1: setIsOpen
  } = useState(false);
  const {
    0: isLoading,
    1: setIsLoading
  } = useState(false);
  const {
    0: isInitialized,
    1: setIsInitialized
  } = useState(false);
  const {
    0: selectedOption,
    1: setSelectedOption
  } = useState(value);
  const {
    0: hoveredGroupKey,
    1: setHoveredGroupKey
  } = useState(null);
  const isMobile = useMediaQuery({
    query: "(max-width: ".concat(BREAKPOINTS.md, "px)")
  });
  const {
    cssModules
  } = useCssModules();
  const {
    borderlessInput,
    borderlessInputContainer,
    borderlessInputFocus,
    borderlessInputText,
    borderlessInputLabel,
    borderlessInputLabelSmall,
    borderlessInputClearButton,
    borderlessInputIcon,
    borderlessDropdown,
    borderlessOption,
    borderlessOptionTitle,
    borderlessOptionSubtitle
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.views.mobile.Autocomplete) || {};
  const {
    containerClassName,
    locationPinIconClassName,
    flightIconClassName,
    headerContainerClassName,
    headerTitleClassName,
    headerSubtitleClassName,
    optionContainerClassName,
    optionTitleClassName,
    optionSubtitleClassName
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.globals.locationAutocomplete) || {};
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Initialize input value only once when component mounts
  useEffect(() => {
    if (!isInitialized && value !== null && value !== void 0 && value.title && inputValue === "") {
      setInputValue(value.title);
      setSelectedOption(value);
      setIsInitialized(true);
    }
  }, [value === null || value === void 0 ? void 0 : value.title, isInitialized, inputValue]);

  // Handle value changes after initialization (for form resets)
  useEffect(() => {
    if (isInitialized) {
      if (value !== null && value !== void 0 && value.title) {
        setInputValue(value.title);
        setSelectedOption(value);
      } else if (value === undefined || value === null) {
        setInputValue("");
        setSelectedOption(undefined);
      }
    }
  }, [value, isInitialized]);

  // Fetch options when input changes
  useEffect(() => {
    if (inputValue.length >= 2) {
      // Don't show dropdown if the input value matches the selected option
      if (selectedOption && inputValue === selectedOption.title) {
        setOptions([]);
        setIsOpen(false);
        return;
      }
      setIsLoading(true);
      getOptions(inputValue).then(result => {
        setOptions(Array.isArray(result) ? result : []);
        setIsOpen(true); // Always open dropdown when we have results (even if empty)
      }).catch(error => {
        console.error("Error fetching options:", error);
        setOptions([]);
        setIsOpen(true); // Open dropdown even on error to show "no options"
      }).finally(() => {
        setIsLoading(false);
      });
    } else {
      setOptions([]);
      setIsOpen(false);
    }
  }, [inputValue, getOptions, selectedOption]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleInputChange = React.useCallback(e => {
    const newValue = e.target.value;
    setInputValue(newValue);
  }, []);
  const handleInputBlur = () => {
    // Don't close dropdown on blur - let click outside handler manage it
  };
  const handleSelectOption = option => {
    setInputValue(option.title);
    onSelectOption(option);
    setIsOpen(false); // Close dropdown immediately
    setSelectedOption(option);
    setOptions([]); // Clear options to prevent showing "no options"
  };
  const handleClearValue = () => {
    setInputValue("");
    onSelectOption(undefined);
    setIsOpen(false);
    setSelectedOption(undefined);
    setOptions([]);
  };

  // Group options by group_key if groupKey is provided
  const groupOptions = options => {
    if (!groupKey || !options.length) {
      return {
        grouped: false,
        groups: [],
        ungrouped: options
      };
    }
    const grouped = {};
    const ungrouped = [];
    options.forEach(option => {
      const groupValue = option.group_key;
      if (groupValue) {
        if (!grouped[groupValue]) {
          grouped[groupValue] = [];
        }
        grouped[groupValue].push(option);
      } else {
        ungrouped.push(option);
      }
    });
    const groups = Object.entries(grouped).map(_ref2 => {
      let [key, options] = _ref2;
      return {
        key,
        options
      };
    });
    return {
      grouped: groups.length > 0,
      groups,
      ungrouped
    };
  };

  // Default option renderer
  const renderOption = (option, index) => {
    const isHovered = hoveredGroupKey === option.group_key;
    return __jsx("div", {
      key: "".concat(option.id, "-").concat(index),
      className: cn("flex items-center cursor-pointer hover:bg-general-hovers", useBorderlessStyle && borderlessOption ? borderlessOption : !isMobile && optionContainerClassName),
      onClick: e => {
        e.preventDefault();
        e.stopPropagation();
        handleSelectOption(option);
      },
      onMouseEnter: () => setHoveredGroupKey(option.group_key || null),
      onMouseLeave: () => setHoveredGroupKey(null)
    }, optionsIcon && __jsx("div", {
      className: cn(flightIconClassName)
    }, optionsIcon), __jsx("div", null, __jsx("p", {
      className: cn(optionTitleClassName, isHovered && "text-general-primary")
    }, option.title), option.subTitle && __jsx("p", {
      className: optionSubtitleClassName
    }, option.subTitle)));
  };

  // Group header renderer
  const renderGroupHeader = groupKey => {
    // Parse the group key to extract municipality and full location
    const parts = groupKey.split(', ');
    const municipality = parts[0] || groupKey;
    const fullLocation = groupKey;
    const isHovered = hoveredGroupKey === groupKey;
    return __jsx("div", {
      key: "group-".concat(groupKey),
      className: cn(headerContainerClassName)
    }, groupIcon && __jsx("div", {
      className: locationPinIconClassName
    }, groupIcon), __jsx("div", null, __jsx("div", {
      className: cn(headerTitleClassName)
    }, municipality), __jsx("div", {
      className: headerSubtitleClassName
    }, fullLocation)));
  };
  const renderGroupedOptions = () => {
    const {
      grouped,
      groups,
      ungrouped
    } = groupOptions(options);
    if (!grouped) {
      return options.map((option, index) => renderOption(option, index));
    }
    const renderedElements = [];

    // Render grouped options
    groups.forEach(_ref3 => {
      let {
        key,
        options: groupOptions
      } = _ref3;
      renderedElements.push(renderGroupHeader(key));
      groupOptions.forEach((option, index) => {
        renderedElements.push(renderOption(option, index));
      });
    });

    // Render ungrouped options at the end
    ungrouped.forEach((option, index) => {
      renderedElements.push(renderOption(option, index + groups.length * 100)); // Ensure unique keys
    });
    return renderedElements;
  };
  const shouldShowDropdown = isOpen && !disablePopper && inputValue.length >= 2;
  return __jsx("div", {
    ref: containerRef,
    className: cn("relative", className)
  }, __jsx(CustomInput, {
    ref: inputRef,
    value: inputValue,
    onChange: handleInputChange,
    onBlur: handleInputBlur,
    onClick: useBorderlessStyle ? undefined : onClick,
    placeholder: placeholder || label,
    label: disableLabelAnimation ? "" : label,
    error: hasErrors,
    helperText: errorMessage,
    disabled: disable,
    autoComplete: "off",
    className: cn(useBorderlessStyle && borderlessInput ? borderlessInput : ""),
    containerClassName: cn(useBorderlessStyle && borderlessInputContainer ? borderlessInputContainer : ""),
    dataTestId: dataTestId,
    disableLabelAnimation: disableLabelAnimation,
    startAdornment: inputLabelIcon,
    endAdornment: __jsx("div", {
      className: "flex items-center"
    }, isLoading && options.length > 0 && __jsx("div", {
      className: "w-4 h-4 mr-2"
    }, __jsx("div", {
      className: "animate-spin rounded-full h-4 w-4 border-2 text-general-general_text border-t-transparent"
    })), inputValue.length > 0 && !disable && !disableXButton && __jsx(IconButton, {
      onClickButton: handleClearValue,
      icon: __jsx(Icons.xIcon, {
        size: 20,
        className: "text-general-general_text"
      })
    }))
  }), shouldShowDropdown && __jsx("div", {
    className: cn(containerClassName, useBorderlessStyle && borderlessDropdown ? borderlessDropdown : "")
  }, options.length === 0 ? __jsx("div", {
    className: cn(optionSubtitleClassName, "p-4")
  }, noOptionsText) : renderGroupedOptions()));
};
export default CustomAutocomplete;